import { forEach, map } from "lodash";
import { v4 as uuidV4 } from "uuid";

import { getDefaultResource } from "src/integrationNode/integrationResources/DefaultResources";
import {
  DEFAULT_OUTPUT_BE_NAME,
  InputMappingGroup,
  InputMappingList,
  InputMappings,
  InputMapping,
  RenderCondition,
  IntegrationResourceBE,
  IntegrationResource,
  MultiSelector,
  SingleMappingBE,
} from "src/integrationNode/types";
import { logger } from "src/utils/logger";

export const integrationResourceBeToFe = (
  reportBE: IntegrationResourceBE,
): IntegrationResource => {
  const reportFE = getDefaultResource(
    reportBE.provider_resource,
    reportBE.connection_id,
    reportBE.resource_config_id,
  );

  const setFeInputMappingFields = (
    feMapping: InputMapping,
    beMapping?: SingleMappingBE,
  ) => {
    if (beMapping) {
      feMapping.id = beMapping?.id;
      feMapping.assignedTo = beMapping.expression;
    }
  };

  const files = Object.fromEntries(
    Object.entries(reportBE.files || {}).map(([key, value]) => [
      key,
      {
        id: value.id,
        displayName: "File",
        assignedTo: value.mapped_to,
        selected: value.active,
      },
    ]),
  );
  reportFE.files = files;

  //ungrouped
  forEach(reportFE.input.ungrouped, (inputMapping, beName) => {
    setFeInputMappingFields(inputMapping, reportBE.input.singles[beName]);
  });

  //grouped
  forEach(reportFE.input.grouped, (group, groupName) => {
    const beGroup = reportBE.input.groups[groupName];
    if (beGroup) {
      group.elements = group.getDefaultElements();
      forEach(group.elements, (inputMapping, beName) => {
        setFeInputMappingFields(inputMapping, beGroup.singles[beName]);
      });
      if (group.lists) {
        forEach(group.lists, (list, listName) => {
          list.elements = [];
          const beList = beGroup.lists[listName];

          forEach(beList, (beListElement) => {
            const newElement = { ...list.getDefaultElement() };
            // Hard to refactor this at the moment, requires more time and tests
            // eslint-disable-next-line sonarjs/no-nested-functions
            forEach(newElement, (inputMapping, beName) => {
              setFeInputMappingFields(
                inputMapping,
                beListElement.singles[beName],
              );
            });
            list.elements.push(newElement);
          });
        });
      }
    }
  });

  //list
  //for each list in the frontend
  forEach(reportFE.input.lists, (list, listName) => {
    list.elements = [];
    const beList = reportBE.input.lists[listName];
    //for each list element in the corresponding backend list,
    //create that element in the frontend list
    forEach(beList, (beListElement) => {
      const newElement = { ...list.getDefaultElement() };
      forEach(newElement, (inputMapping, beName) => {
        setFeInputMappingFields(inputMapping, beListElement.singles[beName]);
      });
      list.elements.push(newElement);
    });
  });

  //multiselect
  forEach(reportFE.input.multiselectors, (mulitSelectInput, beName) => {
    mulitSelectInput.id = reportBE.input.singles[beName].id;
    mulitSelectInput.selected = JSON.parse(
      reportBE.input.singles[beName].expression,
    );
  });

  //outputs
  forEach(reportBE.output, (mappingBE, beName) => {
    if (beName === DEFAULT_OUTPUT_BE_NAME) {
      reportFE.output[DEFAULT_OUTPUT_BE_NAME].id = mappingBE.id;
      reportFE.output[DEFAULT_OUTPUT_BE_NAME].assignedTo = mappingBE.mapped_to;
      reportFE.output[DEFAULT_OUTPUT_BE_NAME].selected = mappingBE.active;
    } else if (reportFE.output.insights[beName]) {
      reportFE.output.insights[beName].id = mappingBE.id;
      reportFE.output.insights[beName].assignedTo = mappingBE.mapped_to;
      reportFE.output.insights[beName].selected = mappingBE.active;
    } else {
      logger.error(`Unknown insight: ${beName}`);
    }
  });

  reportFE.config = {
    // Because this field was newly added we set an empty one if the BE config does not already include it
    tenant_id: {
      id: uuidV4(),
      expression: "",
    },
    timeout: {
      active: false,
      timeout_seconds: "15",
    },
    ...reportBE.config,
  };
  return reportFE;
};

export const inputMappingsFeToBe = (mappingsFe: {
  [key: string]: InputMapping;
}): { [key: string]: SingleMappingBE } =>
  Object.fromEntries(
    map(mappingsFe, (inputMapping, beName) => [
      beName,
      { id: inputMapping.id, expression: inputMapping.assignedTo },
    ]),
  );

export const multiselectsFeToBe = (multiselectorsFe?: {
  [key: string]: MultiSelector;
}): { [key: string]: SingleMappingBE } =>
  Object.fromEntries(
    map(multiselectorsFe, (multiSelectorInput, beName) => [
      beName,
      {
        id: multiSelectorInput.id,
        expression: JSON.stringify(multiSelectorInput.selected),
      },
    ]),
  );

export const integrationResourceFeToBe = (
  resourceFE: IntegrationResource,
): IntegrationResourceBE => {
  return {
    provider_resource: resourceFE.providerResource,
    connection_id: resourceFE.connectionId,
    resource_config_id: resourceFE.resourceConfigId,
    input: {
      singles: {
        ...inputMappingsFeToBe(resourceFE.input.ungrouped),
        ...multiselectsFeToBe(resourceFE.input.multiselectors),
      },
      groups: Object.fromEntries(
        map(resourceFE.input.grouped, (group, groupName) => [
          groupName,
          group.elements
            ? {
                singles: inputMappingsFeToBe(group.elements),
                groups: {},
                lists: Object.fromEntries(
                  map(group.lists, (list, listName) => [
                    listName,
                    list.elements.map((element) => ({
                      singles: inputMappingsFeToBe(element),
                      groups: {},
                      lists: {},
                    })),
                  ]),
                ),
              }
            : undefined,
        ]).filter(([, value]) => value),
      ),
      lists: Object.fromEntries(
        map(resourceFE.input.lists, (list, listName) => [
          listName,
          list.elements.map((element) => ({
            singles: inputMappingsFeToBe(element),
            groups: {},
            lists: {},
          })),
        ]),
      ),
    },
    output: {
      [DEFAULT_OUTPUT_BE_NAME]: {
        id: resourceFE.output[DEFAULT_OUTPUT_BE_NAME].id,
        mapped_to: resourceFE.output[DEFAULT_OUTPUT_BE_NAME].assignedTo,
        active: resourceFE.output[DEFAULT_OUTPUT_BE_NAME].selected,
      },
      ...Object.fromEntries(
        map(resourceFE.output.insights, (outputMapping, beName) => [
          beName,
          {
            id: outputMapping.id,
            mapped_to: outputMapping.assignedTo,
            active: outputMapping.selected,
          },
        ]),
      ),
    },
    config: resourceFE.config,
    files: Object.fromEntries(
      Object.entries(resourceFE.files || {}).map(([key, value]) => [
        key,
        { id: value.id, mapped_to: value.assignedTo, active: value.selected },
      ]),
    ),
  };
};

const filterInputMapping = (
  group:
    | { [key: string]: InputMapping }
    | { [key: string]: InputMappingGroup }
    | { [key: string]: InputMappingList }
    | { [key: string]: MultiSelector },
  ungrouped: { [key: string]: InputMapping },
): { [key: string]: InputMappingGroup } => {
  if (!group) {
    return group;
  }
  return Object.fromEntries(
    Object.entries(group).filter(([_, value]) => {
      if (value.renderConditions) {
        return value.renderConditions.every(
          (renderCondition: RenderCondition) => {
            // Only keep this item if its renderCondition key
            // meets the specified value condition
            const conditionKey = renderCondition.key;
            const conditionValue = renderCondition.value;
            const referencedInput = ungrouped[conditionKey];

            return (
              referencedInput &&
              conditionValue.includes(referencedInput.assignedTo)
            );
          },
        );
      }
      // If no renderCondition, keep the item
      return true;
    }),
  );
};

export const filterInputs = (input: InputMappings): InputMappings => {
  let filteredInputs = {};

  Object.entries(input).forEach(([inputGroup, groupValues]) => {
    switch (inputGroup) {
      case "ungrouped":
        filteredInputs = {
          ...filteredInputs,
          ungrouped: filterInputMapping(groupValues, input.ungrouped),
        };
        break;
      case "multiselectors":
        filteredInputs = {
          ...filteredInputs,
          multiselectors: filterInputMapping(groupValues, input.ungrouped),
        };
        break;
      case "grouped":
        filteredInputs = {
          ...filteredInputs,
          grouped: filterInputMapping(groupValues, input.ungrouped),
        };
        break;
      case "lists":
        filteredInputs = {
          ...filteredInputs,
          lists: filterInputMapping(groupValues, input.ungrouped),
        };
        break;
    }
  });

  return filteredInputs as InputMappings;
};
